import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { FeatureGate } from "@/components/FeatureGate";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Plus, TrendingUp, TrendingDown, Wallet } from "lucide-react";
import { brl, dateBR } from "@/lib/format";
import { toast } from "sonner";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

export default function Finance() {
  return <FeatureGate feature="expenses" title="Financeiro completo a partir do plano Essencial"><Inner /></FeatureGate>;
}

function Inner() {
  const [entries, setEntries] = useState([]);
  const [sum, setSum] = useState(null);
  const [dlg, setDlg] = useState(false);
  const [f, setF] = useState({ kind: "despesa", description: "", amount: 0, category: "Geral", paid: false });

  const load = async () => {
    const [e, s] = await Promise.all([api.get("/financial/entries"), api.get("/financial/summary")]);
    setEntries(e.data); setSum(s.data);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    try { await api.post("/financial/entries", { ...f, amount: parseFloat(f.amount) });
      toast.success("Lançamento criado"); setDlg(false); setF({ kind: "despesa", description: "", amount: 0, category: "Geral", paid: false }); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };

  return (
    <div className="space-y-6" data-testid="finance-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div><h1 className="font-display text-3xl font-extrabold text-stone-900">Financeiro</h1>
          <p className="text-stone-500">Fluxo de caixa, contas a pagar/receber.</p></div>
        <Dialog open={dlg} onOpenChange={setDlg}>
          <DialogTrigger asChild><Button className="gap-2 bg-orange-600 hover:bg-orange-700" data-testid="new-entry-btn"><Plus className="h-4 w-4" /> Novo lançamento</Button></DialogTrigger>
          <DialogContent><DialogHeader><DialogTitle>Novo lançamento</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Tipo</Label>
                <select className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm" value={f.kind} onChange={(e) => setF({ ...f, kind: e.target.value })} data-testid="entry-kind">
                  <option value="despesa">Despesa</option><option value="receita">Receita</option>
                </select></div>
              <div><Label>Descrição</Label><Input value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} data-testid="entry-desc" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Valor</Label><Input type="number" step="0.01" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} data-testid="entry-amount" /></div>
                <div><Label>Categoria</Label><Input value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} /></div>
              </div>
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.paid} onChange={(e) => setF({ ...f, paid: e.target.checked })} /> Já pago</label>
            </div>
            <DialogFooter><Button onClick={save} className="bg-orange-600 hover:bg-orange-700" data-testid="entry-save">Salvar</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card><CardContent className="p-5"><TrendingUp className="h-5 w-5 text-emerald-600 mb-2" />
          <div className="text-xs text-stone-500 font-semibold uppercase">Receitas pagas</div>
          <div className="font-mono-num text-2xl font-extrabold text-emerald-600">{brl(sum?.receitas_pagas)}</div></CardContent></Card>
        <Card><CardContent className="p-5"><TrendingUp className="h-5 w-5 text-amber-600 mb-2" />
          <div className="text-xs text-stone-500 font-semibold uppercase">A receber</div>
          <div className="font-mono-num text-2xl font-extrabold text-amber-600">{brl(sum?.receitas_pendentes)}</div></CardContent></Card>
        <Card><CardContent className="p-5"><TrendingDown className="h-5 w-5 text-rose-600 mb-2" />
          <div className="text-xs text-stone-500 font-semibold uppercase">Despesas pagas</div>
          <div className="font-mono-num text-2xl font-extrabold text-rose-600">{brl(sum?.despesas_pagas)}</div></CardContent></Card>
        <Card><CardContent className="p-5"><Wallet className="h-5 w-5 text-orange-600 mb-2" />
          <div className="text-xs text-stone-500 font-semibold uppercase">Lucro</div>
          <div className={`font-mono-num text-2xl font-extrabold ${(sum?.lucro || 0) >= 0 ? "text-stone-900" : "text-rose-600"}`}>{brl(sum?.lucro)}</div></CardContent></Card>
      </div>
      <Tabs defaultValue="all">
        <TabsList><TabsTrigger value="all" data-testid="tab-all">Todos</TabsTrigger>
          <TabsTrigger value="receita" data-testid="tab-receita">Receitas</TabsTrigger>
          <TabsTrigger value="despesa" data-testid="tab-despesa">Despesas</TabsTrigger></TabsList>
        {["all", "receita", "despesa"].map((k) => (
          <TabsContent key={k} value={k}>
            <Card><CardContent className="p-0">
              <table className="w-full text-sm">
                <thead className="bg-stone-50 text-stone-500 text-xs uppercase"><tr>
                  <th className="text-left px-4 py-3">Data</th><th className="text-left px-4 py-3">Descrição</th>
                  <th className="text-left px-4 py-3">Categoria</th><th className="text-right px-4 py-3">Valor</th>
                  <th className="text-center px-4 py-3">Status</th></tr></thead>
                <tbody>{entries.filter((e) => k === "all" || e.kind === k).map((e) => (
                  <tr key={e.id} className="border-t border-stone-100" data-testid={`entry-${e.id}`}>
                    <td className="px-4 py-3">{dateBR(e.created_at)}</td>
                    <td className="px-4 py-3">{e.description}</td>
                    <td className="px-4 py-3 text-stone-500">{e.category}</td>
                    <td className={`px-4 py-3 text-right font-mono-num font-bold ${e.kind === "receita" ? "text-emerald-600" : "text-rose-600"}`}>{e.kind === "receita" ? "+" : "-"}{brl(e.amount)}</td>
                    <td className="px-4 py-3 text-center">{e.paid ? <span className="text-xs text-emerald-600 font-semibold">Pago</span> : <span className="text-xs text-amber-600 font-semibold">Pendente</span>}</td>
                  </tr>
                ))}</tbody>
              </table>
            </CardContent></Card>
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}
